import { useState } from 'react';
import { Check, Pencil, Plus, Trash2 } from 'lucide-react';
import { formatCurrency } from '../lib/format.js';
import { VAT_PRESETS, MULTI_RATE_PRESETS, computeVatFromTtc, formatRateFr } from '../lib/vat.js';
import { useTransactionVatMutation } from '../hooks/useTransactionVatMutation.js';
import { isVatApplicable } from '../lib/transaction-utils.js';

const MAX_LINES = 6;
const MIN_LINES = 2;

function emptyLine(rateChoice = '') {
  return { rateChoice: rateChoice === '' ? '' : String(rateChoice), customRate: '', amountInput: '' };
}

function defaultMultiLines() {
  return [emptyLine(20), emptyLine(10)];
}

/**
 * Pré-remplissage du mode taux unique depuis la transaction : le montant affiché
 * reste calculé depuis le taux (comme avant cette extraction) — vatManualMode
 * repart toujours à false, une bascule volontaire (crayon) est nécessaire pour
 * voir/éditer un montant qui diverge du calcul.
 */
function initSingleFromTx(tx) {
  if (tx?.vat_rate != null) {
    const preset = VAT_PRESETS.find((p) => p.value === tx.vat_rate);
    return {
      vatRateSelected: preset ? tx.vat_rate : 'custom',
      vatRateCustom: preset ? '' : String(tx.vat_rate),
      vatAmountManualInput: '',
      vatManualMode: false,
      vatAmountOverride: null,
    };
  }
  if (tx?.vat_amount_cents != null) {
    return {
      vatRateSelected: 'amount',
      vatRateCustom: '',
      vatAmountManualInput: (Math.abs(tx.vat_amount_cents) / 100).toFixed(2),
      vatManualMode: false,
      vatAmountOverride: null,
    };
  }
  return emptySingle();
}

function emptySingle() {
  return { vatRateSelected: '', vatRateCustom: '', vatAmountManualInput: '', vatManualMode: false, vatAmountOverride: null };
}

/**
 * Pré-remplissage du mode taux unique depuis une SUGGESTION lue sur un justificatif :
 * contrairement à la transaction enregistrée, le montant proposé est affiché tel quel
 * (mode manuel activé) puisqu'il peut différer légèrement du calcul TTC × taux/(100+taux).
 */
function initSingleFromSuggestion(suggestion) {
  if (suggestion?.vat_rate != null) {
    const preset = VAT_PRESETS.find((p) => p.value === suggestion.vat_rate);
    const hasAmount = suggestion.vat_amount_cents != null;
    return {
      vatRateSelected: preset ? suggestion.vat_rate : 'custom',
      vatRateCustom: preset ? '' : String(suggestion.vat_rate),
      vatAmountManualInput: hasAmount ? (Math.abs(suggestion.vat_amount_cents) / 100).toFixed(2) : '',
      vatManualMode: hasAmount,
      vatAmountOverride: hasAmount ? Math.abs(suggestion.vat_amount_cents) : null,
    };
  }
  if (suggestion?.vat_amount_cents != null) {
    return {
      vatRateSelected: 'amount',
      vatRateCustom: '',
      vatAmountManualInput: (Math.abs(suggestion.vat_amount_cents) / 100).toFixed(2),
      vatManualMode: false,
      vatAmountOverride: Math.abs(suggestion.vat_amount_cents),
    };
  }
  return emptySingle();
}

function linesFromBreakdown(breakdown) {
  return breakdown.map((b) => {
    const isPreset = MULTI_RATE_PRESETS.includes(b.rate);
    return {
      rateChoice: isPreset ? String(b.rate) : b.rate != null ? 'custom' : '',
      customRate: isPreset || b.rate == null ? '' : String(b.rate),
      amountInput: b.vat_amount_cents != null ? (Math.abs(b.vat_amount_cents) / 100).toFixed(2) : '',
    };
  });
}

/** Dérive l'état initial (mode + valeurs) depuis la transaction et une suggestion éventuelle. */
function deriveState(tx, suggestion) {
  const src = suggestion ?? tx;
  const breakdown = src?.vat_breakdown;
  if (Array.isArray(breakdown) && breakdown.length >= 2) {
    return { mode: 'multi', single: emptySingle(), multiLines: linesFromBreakdown(breakdown) };
  }
  const single = suggestion ? initSingleFromSuggestion(suggestion) : initSingleFromTx(tx);
  return { mode: 'single', single, multiLines: defaultMultiLines() };
}

function parseAmountToCents(raw) {
  if (raw === '' || raw == null) return null;
  const parsed = Math.round(parseFloat(String(raw).replace(',', '.')) * 100);
  return Number.isNaN(parsed) ? null : parsed;
}

function lineEffectiveRate(line) {
  if (line.rateChoice === '') return null;
  if (line.rateChoice === 'custom') {
    const r = parseFloat(line.customRate.replace(',', '.'));
    return Number.isNaN(r) || r < 0 || r > 100 ? null : r;
  }
  return Number(line.rateChoice);
}

/**
 * Éditeur de TVA d'une transaction — taux unique (pastilles, auto-save) ou multi-taux
 * (une ligne par taux, enregistrement explicite). Supporte l'affichage d'une suggestion
 * lue sur un justificatif (non enregistrée tant que l'utilisateur ne valide pas).
 *
 * @param {object} transaction
 * @param {{amount_cents?: number, vat_rate?: number|null, vat_amount_cents?: number|null, vat_breakdown?: Array|null}|null} [suggestion]
 * @param {() => void} [onDismissSuggestion]
 */
export default function VatEditor({ transaction, suggestion = null, onDismissSuggestion }) {
  const tx = transaction;
  const vatMutation = useTransactionVatMutation();

  const initial = deriveState(tx, suggestion);
  const [mode, setMode] = useState(initial.mode);
  const [vatRateSelected, setVatRateSelected] = useState(initial.single.vatRateSelected);
  const [vatRateCustom, setVatRateCustom] = useState(initial.single.vatRateCustom);
  const [vatAmountOverride, setVatAmountOverride] = useState(initial.single.vatAmountOverride);
  const [vatAmountManualInput, setVatAmountManualInput] = useState(initial.single.vatAmountManualInput);
  const [vatManualMode, setVatManualMode] = useState(initial.single.vatManualMode);
  const [multiLines, setMultiLines] = useState(initial.multiLines);
  const [vatSaveSuccess, setVatSaveSuccess] = useState(false);
  // Trace la référence de suggestion déjà appliquée à l'état local, pour ré-initialiser
  // les champs si une NOUVELLE suggestion arrive (ou disparaît) sans que la transaction
  // ne change (le parent ne remonte le composant que sur changement de transaction).
  // `dismissed` est distinct : « Ignorer » ne change pas la référence `suggestion` reçue
  // (le parent la nettoiera à son rythme), donc on la masque localement sans redéclencher
  // la resynchronisation ci-dessous tant que le parent n'envoie pas une suggestion différente.
  const [appliedSuggestion, setAppliedSuggestion] = useState(suggestion);
  const [dismissed, setDismissed] = useState(false);

  if (suggestion !== appliedSuggestion) {
    const next = deriveState(tx, suggestion);
    setAppliedSuggestion(suggestion);
    setDismissed(false);
    setMode(next.mode);
    setVatRateSelected(next.single.vatRateSelected);
    setVatRateCustom(next.single.vatRateCustom);
    setVatAmountOverride(next.single.vatAmountOverride);
    setVatAmountManualInput(next.single.vatAmountManualInput);
    setVatManualMode(next.single.vatManualMode);
    setMultiLines(next.multiLines);
    setVatSaveSuccess(false);
  }

  if (!tx || !isVatApplicable(tx)) return null;

  const amount = Math.abs(tx.amount_cents ?? 0);
  const suggestionActive = suggestion != null && !dismissed;

  async function saveVat(payload) {
    setVatSaveSuccess(false);
    try {
      await vatMutation.mutateAsync({ id: tx.id, ...payload });
      setVatSaveSuccess(true);
      setTimeout(() => setVatSaveSuccess(false), 1500);
      // Un enregistrement réussi depuis une suggestion la referme : c'est au parent
      // de ne plus la fournir (il retire son état local) — cf. handleIgnoreSuggestion.
      if (suggestionActive) onDismissSuggestion?.();
    } catch { /* erreur via vatMutation.error */ }
  }

  // ── Mode taux unique ──────────────────────────────────────────────────────

  const amountMode = vatRateSelected === 'amount';
  const customRateParsed = parseFloat(vatRateCustom);
  const customRateInvalid =
    vatRateSelected === 'custom' &&
    vatRateCustom !== '' &&
    (Number.isNaN(customRateParsed) || customRateParsed < 0 || customRateParsed > 100);
  const effectiveRate = vatRateSelected === 'custom'
    ? (customRateInvalid || vatRateCustom === '' ? null : customRateParsed)
    : vatRateSelected !== '' && !amountMode
      ? Number(vatRateSelected)
      : null;

  const autoVatCents = effectiveRate != null ? computeVatFromTtc(amount, effectiveRate) : null;
  const storedAmountCents = tx.vat_amount_cents != null ? Math.abs(tx.vat_amount_cents) : null;
  const displayVatCents = amountMode
    ? (vatAmountOverride ?? storedAmountCents)
    : vatManualMode && vatAmountOverride != null
      ? vatAmountOverride
      : autoVatCents;

  const amountInvalid =
    vatAmountOverride != null && (vatAmountOverride < 0 || vatAmountOverride > amount);

  async function saveRate(rate, amountOverride) {
    await saveVat({ vat_rate: rate, vat_amount_cents: amountOverride ?? undefined });
  }

  async function handlePresetClick(val) {
    if (val === 'multi') {
      setMode('multi');
      return;
    }
    setVatRateSelected(val);
    setVatAmountOverride(null);
    setVatManualMode(false);
    if (val === 'amount') {
      setVatAmountManualInput(displayVatCents != null ? (displayVatCents / 100).toFixed(2) : '');
    } else if (val !== 'custom' && !suggestionActive) {
      await saveRate(Number(val));
    }
  }

  function handleCustomBlur() {
    if (suggestionActive) return;
    const rate = parseFloat(vatRateCustom);
    if (!Number.isNaN(rate) && rate >= 0 && rate <= 100) saveRate(rate);
  }

  async function handleManualBlur() {
    if (suggestionActive) return;
    if (vatAmountOverride == null || amountInvalid) return;
    if (amountMode) {
      await saveVat({ vat_rate: null, vat_amount_cents: vatAmountOverride });
    } else if (effectiveRate != null) {
      await saveRate(effectiveRate, vatAmountOverride);
    }
  }

  function handleManualAmountToggle() {
    if (!vatManualMode) {
      setVatAmountManualInput(autoVatCents != null ? (autoVatCents / 100).toFixed(2) : '');
    }
    setVatManualMode((v) => !v);
    if (vatManualMode) setVatAmountOverride(null);
  }

  function handleManualAmountChange(e) {
    const raw = e.target.value;
    setVatAmountManualInput(raw);
    setVatAmountOverride(parseAmountToCents(raw));
  }

  async function handleClearVat() {
    try {
      await vatMutation.mutateAsync({ id: tx.id, vat_rate: null, vat_amount_cents: null });
      resetToEmpty();
      setVatSaveSuccess(true);
      setTimeout(() => setVatSaveSuccess(false), 1500);
    } catch { /* erreur via vatMutation.error */ }
  }

  function resetToEmpty() {
    setMode('single');
    setVatRateSelected('');
    setVatRateCustom('');
    setVatAmountOverride(null);
    setVatAmountManualInput('');
    setVatManualMode(false);
    setMultiLines(defaultMultiLines());
  }

  // Enregistrement explicite (mode taux unique + suggestion active).
  async function handleSaveSingleSuggestion() {
    if (amountMode) {
      if (vatAmountOverride == null || amountInvalid) return;
      await saveVat({ vat_rate: null, vat_amount_cents: vatAmountOverride });
    } else if (effectiveRate != null) {
      const overrideCents = vatManualMode ? vatAmountOverride : undefined;
      if (vatManualMode && (vatAmountOverride == null || amountInvalid)) return;
      await saveRate(effectiveRate, overrideCents);
    }
  }

  // ── Mode multi-taux ───────────────────────────────────────────────────────

  function updateLine(idx, patch) {
    setMultiLines((lines) => lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  function addLine() {
    setMultiLines((lines) => (lines.length >= MAX_LINES ? lines : [...lines, emptyLine()]));
  }

  function removeLine(idx) {
    setMultiLines((lines) => (lines.length <= MIN_LINES ? lines : lines.filter((_, i) => i !== idx)));
  }

  const resolvedLines = multiLines.map((l) => ({
    rate: lineEffectiveRate(l),
    amountCents: parseAmountToCents(l.amountInput),
  }));

  const multiTotalCents = resolvedLines.reduce((s, l) => s + (l.amountCents ?? 0), 0);

  const multiHasMissing = resolvedLines.some((l) => l.rate == null || l.amountCents == null);
  const multiHasNegative = resolvedLines.some((l) => l.amountCents != null && l.amountCents < 0);
  const definedRates = resolvedLines.map((l) => l.rate).filter((r) => r != null);
  const multiHasDuplicate = new Set(definedRates).size !== definedRates.length;
  const multiExceedsTtc = multiTotalCents > amount;

  let multiError = null;
  if (multiHasDuplicate) multiError = 'Deux lignes ont le même taux — fusionnez-les.';
  else if (multiHasNegative) multiError = 'Le montant de TVA ne peut pas être négatif.';
  else if (multiExceedsTtc) multiError = 'Le total de TVA dépasse le montant de l’opération (TTC).';
  else if (multiHasMissing) multiError = 'Renseignez un taux et un montant pour chaque ligne.';

  const multiSaveDisabled = Boolean(multiError) || vatMutation.isPending;

  async function handleSaveMulti() {
    if (multiSaveDisabled) return;
    const vat_breakdown = resolvedLines.map((l) => ({ rate: l.rate, vat_amount_cents: l.amountCents }));
    await saveVat({ vat_breakdown });
  }

  function handleIgnoreSuggestion() {
    // Ne touche pas `appliedSuggestion` : la prop `suggestion` elle-même ne change pas
    // forcément tout de suite côté parent, `dismissed` suffit à masquer localement.
    const next = deriveState(tx, null);
    setDismissed(true);
    setMode(next.mode);
    setVatRateSelected(next.single.vatRateSelected);
    setVatRateCustom(next.single.vatRateCustom);
    setVatAmountOverride(next.single.vatAmountOverride);
    setVatAmountManualInput(next.single.vatAmountManualInput);
    setVatManualMode(next.single.vatManualMode);
    setMultiLines(next.multiLines);
    onDismissSuggestion?.();
  }

  const suggestionAmountMismatch =
    suggestionActive &&
    suggestion?.amount_cents != null &&
    Math.abs(suggestion.amount_cents) !== amount;

  const wrapperClass = mode === 'multi' ? 'mt-3 space-y-2' : 'mt-3 pl-[60px] space-y-1.5';

  return (
    <div className={wrapperClass}>
      {suggestionActive && (
        <p className="text-[10px] text-sky-700 bg-sky-50 rounded px-2 py-1 inline-block">
          Lu sur le justificatif, à valider
        </p>
      )}
      {suggestionAmountMismatch && (
        <p className="text-[10px] text-amber-700 bg-amber-50 rounded px-2 py-1">
          Le justificatif indique {formatCurrency(Math.abs(suggestion.amount_cents))} pour une opération de{' '}
          {formatCurrency(amount)}
        </p>
      )}

      {mode === 'single' ? (
        <>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mr-1">TVA</span>
            {VAT_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => handlePresetClick(p.value)}
                disabled={vatMutation.isPending}
                className={`text-[11px] px-2 py-0.5 rounded-full ring-1 transition-colors ${
                  vatRateSelected === p.value
                    ? 'bg-slate-800 text-white ring-slate-800'
                    : 'bg-white text-slate-600 ring-slate-200 hover:ring-slate-400'
                } disabled:opacity-50`}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => handlePresetClick('multi')}
              disabled={vatMutation.isPending}
              className="text-[11px] px-2 py-0.5 rounded-full ring-1 bg-white text-slate-600 ring-slate-200 hover:ring-slate-400 disabled:opacity-50"
            >
              Multi-taux
            </button>
            {vatRateSelected === 'custom' && (
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={vatRateCustom}
                onChange={(e) => setVatRateCustom(e.target.value)}
                onBlur={handleCustomBlur}
                placeholder="%"
                autoFocus
                className="w-14 text-[11px] rounded-full border border-slate-300 px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-slate-500"
              />
            )}
            {!suggestionActive && (tx.vat_rate != null || tx.vat_amount_cents != null) && (
              <button
                type="button"
                onClick={handleClearVat}
                disabled={vatMutation.isPending}
                className="text-[10px] text-slate-400 hover:text-red-600 ml-1 underline"
              >
                Effacer
              </button>
            )}
            {vatSaveSuccess && (
              <span className="text-[10px] text-emerald-600 inline-flex items-center gap-0.5">
                <Check size={10} /> enregistré
              </span>
            )}
            {vatMutation.isPending && <span className="text-[10px] text-slate-400">…</span>}
          </div>
          {(amountMode || displayVatCents != null) && (
            <div className="flex items-center gap-3 text-[11px] text-slate-500 tabular-nums flex-wrap">
              {displayVatCents != null && !amountInvalid && (
                <>
                  <span>
                    HT <span className="text-slate-700 font-medium">{formatCurrency(amount - displayVatCents)}</span>
                  </span>
                  <span className="text-slate-300">·</span>
                </>
              )}
              <span>
                TVA{' '}
                {amountMode || vatManualMode ? (
                  <input
                    type="text"
                    value={vatAmountManualInput}
                    onChange={handleManualAmountChange}
                    onBlur={handleManualBlur}
                    onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                    placeholder="0,00 €"
                    autoFocus={amountMode}
                    className="w-16 text-[11px] rounded border border-slate-300 px-1.5 py-0 focus:outline-none focus:ring-1 focus:ring-slate-500 tabular-nums"
                  />
                ) : (
                  <span className="text-slate-700 font-medium">{formatCurrency(displayVatCents)}</span>
                )}
                {!amountMode && (
                  <button
                    type="button"
                    onClick={handleManualAmountToggle}
                    className="ml-1 text-slate-400 hover:text-slate-700"
                    title={vatManualMode ? 'Revenir au calcul auto' : 'Modifier le montant TVA manuellement'}
                  >
                    {vatManualMode ? '↺' : <Pencil size={9} className="inline" />}
                  </button>
                )}
              </span>
              {!suggestionActive && tx.vat_source && tx.vat_source !== 'manual' && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="text-slate-400 text-[10px]">
                    {tx.vat_source === 'qonto_auto' ? 'auto Qonto' : 'auto déduit'}
                  </span>
                </>
              )}
            </div>
          )}
          {customRateInvalid && <p className="text-[10px] text-red-600">Taux invalide — entre 0 et 100 %.</p>}
          {amountInvalid && (
            <p className="text-[10px] text-red-600">
              Montant de TVA invalide — entre 0 et {formatCurrency(amount)} (TTC).
            </p>
          )}
          {vatMutation.error && <p className="text-[10px] text-red-600">{vatMutation.error.message}</p>}
          {suggestionActive && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSaveSingleSuggestion}
                disabled={vatMutation.isPending}
                className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50"
              >
                Enregistrer
              </button>
              <button
                type="button"
                onClick={handleIgnoreSuggestion}
                className="text-[11px] px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                Ignorer
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mr-1">
              TVA — multi-taux
            </span>
            {!suggestionActive && (
              <button
                type="button"
                onClick={handleClearVat}
                disabled={vatMutation.isPending}
                className="text-[10px] text-slate-400 hover:text-red-600 underline"
              >
                Effacer
              </button>
            )}
            {vatSaveSuccess && (
              <span className="text-[10px] text-emerald-600 inline-flex items-center gap-0.5">
                <Check size={10} /> enregistré
              </span>
            )}
          </div>

          <div className="space-y-1.5">
            {multiLines.map((line, i) => (
              <div key={i} className="flex items-center gap-1.5 flex-wrap">
                <select
                  aria-label={`Taux ligne ${i + 1}`}
                  value={line.rateChoice}
                  onChange={(e) => updateLine(i, { rateChoice: e.target.value })}
                  className="rounded-lg border border-slate-300 px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-slate-500"
                >
                  <option value="">Choisir…</option>
                  {MULTI_RATE_PRESETS.map((r) => (
                    <option key={r} value={String(r)}>{formatRateFr(r)} %</option>
                  ))}
                  <option value="custom">Autre</option>
                </select>
                {line.rateChoice === 'custom' && (
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    aria-label={`Taux personnalisé ligne ${i + 1}`}
                    value={line.customRate}
                    onChange={(e) => updateLine(i, { customRate: e.target.value })}
                    placeholder="%"
                    className="w-14 rounded-lg border border-slate-300 px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-slate-500"
                  />
                )}
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label={`Montant TVA ligne ${i + 1} (€)`}
                  value={line.amountInput}
                  onChange={(e) => updateLine(i, { amountInput: e.target.value })}
                  placeholder="0,00 €"
                  className="w-20 rounded-lg border border-slate-300 px-1.5 py-1 text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-slate-500"
                />
                <button
                  type="button"
                  aria-label={`Supprimer la ligne ${i + 1}`}
                  onClick={() => removeLine(i)}
                  disabled={multiLines.length <= MIN_LINES}
                  className="text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-slate-400"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>

          {multiLines.length < MAX_LINES && (
            <button
              type="button"
              onClick={addLine}
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800"
            >
              <Plus size={12} /> Ajouter un taux
            </button>
          )}

          <div className="flex items-center gap-3 text-[11px] text-slate-500 tabular-nums flex-wrap">
            <span>
              HT{' '}
              <span className="text-slate-700 font-medium">
                {formatCurrency(amount - multiTotalCents)}
              </span>
            </span>
            <span className="text-slate-300">·</span>
            <span>
              TVA <span className="text-slate-700 font-medium">{formatCurrency(multiTotalCents)}</span>
            </span>
          </div>

          {multiError && <p className="text-[10px] text-red-600">{multiError}</p>}
          {vatMutation.error && <p className="text-[10px] text-red-600">{vatMutation.error.message}</p>}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSaveMulti}
              disabled={multiSaveDisabled}
              className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Enregistrer
            </button>
            {suggestionActive && (
              <button
                type="button"
                onClick={handleIgnoreSuggestion}
                className="text-[11px] px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                Ignorer
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
